import React, { useContext, useState } from "react";
import Moment from "moment";

import { CustomersContext } from "../../context/customersContext";
import { SalesContext } from "../../context/salesContext";
import { Button } from "../ui";
import FormSaleDoor from "./FormSaleDoor";
import FormSaleGlass from "./FormSaleGlass";
import FormSaleWorkOrder from "./FormSaleWorkOrder";
import SaleDoorRender from "../details/SaleDoorRender";

const salesField = {
    orderDate: Moment().format("YYYY-MM-DD"),
    contractor: "",
    customerId: "",
    po: "",
    product: "",
    productSpecification: {
        doorNotes: [],
        doorSystem: "",
        brickToBrick: "",
        frameSize: { width: "", height: "" },
        jamb: { jambSize: "", jambMaterial: "Vinyl Wrap" },
        sill: "",
        sillExtension: "",
        doorLeft: {
            doorMaterial: "",
            doorBrand: "",
            doorStyle: "",
            paintOut: "",
            paintIn: "",
            doorSlabWidth: "",
            doorSlabHeight: "",
            lock: { lockType: "Standard Double Bore", distanceFromTop: '40-3/8"', lockStyle: "" },
            hinge: { position: "", hingeColour: "", selfClosing: "No" },
            doorInsert: {
                doorInsert: "",
                design: "",
                width: "",
                height: "",
                quantity: "",
                position: "",
            },
            open: "",
        },
        doorMiddle: {
            doorMaterial: "",
            doorBrand: "",
            doorStyle: "",
            paintOut: "",
            paintIn: "",
            doorSlabWidth: "",
            doorSlabHeight: "",
            lock: { lockType: "Standard Double Bore", distanceFromTop: '40-3/8"', lockStyle: "" },
            hinge: { position: "", hingeColour: "", selfClosing: "" },
            doorInsert: {
                doorInsert: "",
                design: "",
                width: "",
                height: "",
                quantity: "",
                position: "",
            },
            open: "",
        },
        doorRight: {
            doorMaterial: "",
            doorBrand: "",
            doorStyle: "",
            paintOut: "",
            paintIn: "",
            doorSlabWidth: "",
            doorSlabHeight: "",
            lock: { lockType: "Standard Double Bore", distanceFromTop: '40-3/8"', lockStyle: "" },
            hinge: { position: "", hingeColour: "", selfClosing: "" },
            doorInsert: {
                doorInsert: "",
                design: "",
                width: "",
                height: "",
                quantity: "",
                position: "",
            },
            open: "",
        },
        sidelightLeft: {
            sidelight: "No",
            panel: "",
            panelWidth: "",
            panelHeight: "",
            panelWidth2: "",
            panelHeight2: "",
            insert: "",
            insertDesign: "",
            insertWidth: "",
            insertHeight: "",
        },
        sidelightRight: {
            sidelight: "No",
            panel: "",
            panelWidth: "",
            panelHeight: "",
            panelWidth2: "",
            panelHeight2: "",
            insert: "",
            insertDesign: "",
            insertWidth: "",
            insertHeight: "",
        },
        transom: {
            transomBox: "No",
            transomType: "",
            transomInsertDesign: "",
            transomArcHeight: "",
            transomArcLegs: "",
            transomRow: "",
            transomColumn: "",
            transomInsertWidth: "",
            transomInsertHeight: "",
        },
    },
    paymentMethod: "",
    cost: "",
    labourCost: "",
    salesPrice: "",
    payments: [],
    notes: [],
    workOrder: { installationRequired: "", status: "Processed" },
    expectedCompletionDate: "",
    completionDate: "",
    recentActivities: [],
};

// ─── Shared input styles ───────────────────────────────────────────────────────

const lbl = {
    display: "block",
    marginBottom: "0.3rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#64748b",
};

const inputBase = {
    width: "100%",
    padding: "0.5rem 0.65rem",
    fontSize: "0.93rem",
    color: "#1e293b",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};

const focusHandlers = {
    onFocus: (e) => {
        e.target.style.borderColor = "#2c7aa0";
        e.target.style.boxShadow = "0 0 0 3px rgba(44,122,160,0.12)";
        e.target.style.background = "#ffffff";
    },
    onBlur: (e) => {
        e.target.style.borderColor = "#e2e8f0";
        e.target.style.boxShadow = "none";
        e.target.style.background = "#f8fafc";
    },
};

const fieldWrap = { marginBottom: "1rem" };

const row2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" };
const row3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" };
const row4 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1rem" };

const sectionHeader = {
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    margin: "1.5rem 0 0.75rem",
    paddingBottom: "0.4rem",
    borderBottom: "1px solid #f1f5f9",
};

const errMsg = { margin: "0.25rem 0 0", fontSize: "0.78rem", color: "#dc2626" };

// ─── Component ────────────────────────────────────────────────────────────────

const FormSale = ({ handleClose }) => {
    const { action } = useContext(SalesContext);
    const { customers } = useContext(CustomersContext);

    const [newSale, setNewSale] = useState(salesField);
    const [payment, setPayment] = useState({ date: Moment(), amount: "" });
    const [note, setNote] = useState({ date: Moment(), description: "" });
    const [saleAdded, setSaleAdded] = useState(false);
    const [showDoorRender, setShowDoorRender] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [renderValidations, setRenderValidations] = useState([]);

    const handleUpdate = (set, field, value) => {
        if (field === "expectedCompletionDate") {
            if (newSale.orderDate === value && newSale.workOrder.installationRequired === "No") {
                setNewSale({
                    ...newSale,
                    completionDate: Moment().format("LL"),
                    workOrder: { ...newSale.workOrder, status: "Completed" },
                });
            } else {
                setNewSale({
                    ...newSale,
                    completionDate: "",
                    workOrder: { ...newSale.workOrder, status: "Processed" },
                });
            }
        }

        if (field === "customerId") {
            customers.forEach((customer) => {
                if (customer._id === value && customer.contractor === "Yes") {
                    set((s) => ({ ...s, [field]: value, contractor: value }));
                } else {
                    set((s) => ({ ...s, [field]: value }));
                }
            });
        } else {
            set((s) => ({ ...s, [field]: value }));
        }
    };

    const handleSelectProduct = (productName) => {
        if (productName === "Glass" && newSale.productSpecification.doorLeft.doorInsert !== "Yes") {
            setNewSale({
                ...newSale,
                product: productName,
                productSpecification: {
                    ...newSale.productSpecification,
                    doorLeft: {
                        ...newSale.productSpecification.doorLeft,
                        doorInsert: {
                            ...newSale.productSpecification.doorLeft.doorInsert,
                            doorInsert: "Yes",
                        },
                    },
                },
            });
        } else {
            setNewSale({ ...newSale, product: productName });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const required = [
            "orderDate",
            "po",
            "customerId",
            "product",
            "expectedCompletionDate",
            "salesPrice",
            "paymentMethod",
        ];
        const missing = required.filter((k) => !newSale[k]);
        const extraErrors = [];

        if (newSale.workOrder.installationRequired === "") extraErrors.push("workOrder");

        if (missing.length || extraErrors.length) {
            setValidationErrors([...missing, ...extraErrors]);
            return;
        }

        try {
            if (payment.amount) newSale.payments.push(payment);
            if (note.description) newSale.notes.push(note);

            // Default paint values
            ["doorLeft", "doorMiddle", "doorRight"].forEach((side) => {
                if (!newSale.productSpecification[side].paintOut)
                    newSale.productSpecification[side].paintOut = "Factory White";
                if (!newSale.productSpecification[side].paintIn)
                    newSale.productSpecification[side].paintIn = "Factory White";
                if (!newSale.productSpecification[side].doorInsert.position)
                    newSale.productSpecification[side].doorInsert.position = "Center";
                if (
                    newSale.productSpecification[side].doorInsert.design &&
                    !newSale.productSpecification[side].doorInsert.doorInsert
                ) {
                    newSale.productSpecification[side].doorInsert.doorInsert = "Yes";
                }
            });

            if (!newSale.productSpecification.transom.transomRow)
                newSale.productSpecification.transom.transomRow = 1;
            if (!newSale.productSpecification.transom.transomColumn)
                newSale.productSpecification.transom.transomColumn = 1;

            await action.addSale(newSale);
            setSaleAdded(true);
            setPayment({ date: Moment(), amount: "" });
            setNote({ date: Moment(), description: "" });
            setNewSale(salesField);
            salesField.payments = [];
            salesField.notes = [];
        } catch (error) {
            console.error("Error adding sale:", error);
        }
    };

    const renderDoor = (event) => {
        event.preventDefault();
        const { frameSize, doorLeft } = newSale.productSpecification;
        const validations = [];
        if (!frameSize.width || !frameSize.height) validations.push("Frame size");
        if (!doorLeft.doorSlabWidth || !doorLeft.doorSlabHeight) validations.push("Door slab size");
        setShowDoorRender(validations.length === 0);
        setRenderValidations(validations);
    };

    const err = (field) => validationErrors.includes(field) && <p style={errMsg}>Required</p>;

    const inputStyle = (field) => ({
        ...inputBase,
        borderColor: validationErrors.includes(field) ? "#fca5a5" : "#e2e8f0",
    });

    const labelStyle = (field) => ({
        ...lbl,
        color: validationErrors.includes(field) ? "#dc2626" : "#64748b",
    });

    const inputField = (labelText, field, value, type = "text") => (
        <div style={fieldWrap}>
            <label style={labelStyle(field)}>{labelText}</label>
            <input
                style={inputStyle(field)}
                type={type}
                name={field}
                value={value}
                onChange={(e) => handleUpdate(setNewSale, field, e.target.value)}
                {...focusHandlers}
            />
            {err(field)}
        </div>
    );

    const productForm = () => {
        if (newSale.product === "Door") {
            return (
                <>
                    <FormSaleDoor
                        newSale={newSale}
                        setNewSale={setNewSale}
                        handleUpdate={handleUpdate}
                        renderDoor={renderDoor}
                        showDoorRender={showDoorRender}
                        validationErrors={validationErrors}
                        renderValidations={renderValidations}
                    />
                    {showDoorRender && <SaleDoorRender newSale={newSale} setNewSale={setNewSale} />}
                </>
            );
        }
        if (newSale.product === "Glass") {
            return (
                <FormSaleGlass
                    newSale={newSale}
                    setNewSale={setNewSale}
                    handleUpdate={handleUpdate}
                />
            );
        }
        if (newSale.product !== "") {
            return (
                <div style={fieldWrap}>
                    <label style={lbl}>Product Type</label>
                    <select
                        style={inputBase}
                        name="product"
                        value={newSale.product}
                        onChange={(e) => handleUpdate(setNewSale, "product", e.target.value)}
                        {...focusHandlers}
                    >
                        <option>Select</option>
                        <option value="Door Slab">Door Slab</option>
                        <option value="Door Lock">Door Lock</option>
                        <option value="Sill">Sill</option>
                        <option value="Other">Other</option>
                    </select>
                </div>
            );
        }
    };

    if (saleAdded) {
        return (
            <div style={{ textAlign: "center", padding: "2.5rem 0" }}>
                <i
                    className="fas fa-check-circle"
                    style={{
                        fontSize: "3rem",
                        color: "#16a34a",
                        marginBottom: "1rem",
                        display: "block",
                    }}
                />
                <p style={{ fontWeight: 700, color: "#15803d", marginBottom: "1.5rem" }}>
                    Sale successfully recorded!
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem" }}>
                    <Button variant="secondary" size="sm" onClick={() => setSaleAdded(false)}>
                        Add Another
                    </Button>
                    {handleClose && (
                        <Button variant="primary" size="sm" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <form id="sale-form" onSubmit={handleSubmit}>
            {validationErrors.length > 0 && (
                <p
                    style={{
                        color: "#dc2626",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        marginBottom: "1rem",
                    }}
                >
                    Please fill out all required fields.
                </p>
            )}

            {/* ── Order Info ── */}
            <p style={sectionHeader}>Order Info</p>
            <div style={row4}>
                {inputField("Order Date", "orderDate", newSale.orderDate, "date")}
                {inputField("PO #", "po", newSale.po)}

                <div style={fieldWrap}>
                    <label style={labelStyle("customerId")}>Customer</label>
                    <select
                        style={inputStyle("customerId")}
                        name="customerId"
                        value={newSale.customerId}
                        onChange={(e) => handleUpdate(setNewSale, "customerId", e.target.value)}
                        {...focusHandlers}
                    >
                        <option value="">Select</option>
                        {customers.map((c, i) => (
                            <option key={i} value={c._id}>
                                {c.firstName} {c.lastName} {c.mobile && `(${c.mobile})`}
                            </option>
                        ))}
                    </select>
                    {err("customerId")}
                </div>

                <div style={fieldWrap}>
                    <label style={lbl}>Contractor</label>
                    <select
                        style={inputBase}
                        name="contractor"
                        value={newSale.contractor}
                        onChange={(e) => handleUpdate(setNewSale, "contractor", e.target.value)}
                        {...focusHandlers}
                    >
                        <option value="">Select</option>
                        {customers
                            .filter((c) => c.contractor === "Yes")
                            .map((c, i) => (
                                <option key={i} value={c._id}>
                                    {c.firstName} {c.lastName} {c.mobile && `(${c.mobile})`}
                                </option>
                            ))}
                    </select>
                </div>
            </div>

            {/* ── Product ── */}
            <p style={sectionHeader}>Product</p>
            <div style={{ ...fieldWrap, marginBottom: "1.25rem" }}>
                <label style={labelStyle("product")}>Select a Product</label>
                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
                    {["Door", "Glass", "Other"].map((p) => {
                        const selected = newSale.product === p;
                        return (
                            <button
                                key={p}
                                type="button"
                                onClick={() => handleSelectProduct(p)}
                                style={{
                                    padding: "0.45rem 1.25rem",
                                    borderRadius: "9999px",
                                    border: `1px solid ${selected ? "#2c7aa0" : "#e2e8f0"}`,
                                    background: selected ? "#2c7aa0" : "#f8fafc",
                                    color: selected ? "#fff" : "#475569",
                                    fontWeight: 600,
                                    fontSize: "0.88rem",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                }}
                            >
                                {p}
                            </button>
                        );
                    })}
                </div>
                {err("product")}
            </div>

            {productForm()}

            {/* ── Installation ── */}
            {newSale.product !== "" && (
                <FormSaleWorkOrder
                    newSale={newSale}
                    setNewSale={setNewSale}
                    handleUpdate={handleUpdate}
                    validationErrors={validationErrors}
                />
            )}

            {/* ── Dates ── */}
            {newSale.product !== "" && (
                <>
                    <p style={sectionHeader}>Schedule</p>
                    <div style={{ maxWidth: "14rem" }}>
                        {inputField(
                            "Expected Completion Date",
                            "expectedCompletionDate",
                            newSale.expectedCompletionDate,
                            "date",
                        )}
                    </div>
                </>
            )}

            {/* ── Payment ── */}
            {newSale.product !== "" && (
                <>
                    <p style={sectionHeader}>Payment</p>
                    <div style={row3}>
                        <div style={fieldWrap}>
                            <label style={lbl}>Cost</label>
                            <input
                                style={inputBase}
                                type="number"
                                name="cost"
                                value={newSale.cost}
                                onChange={(e) => handleUpdate(setNewSale, "cost", e.target.value)}
                                {...focusHandlers}
                            />
                        </div>

                        <div style={fieldWrap}>
                            <label style={labelStyle("salesPrice")}>Sales Price</label>
                            <input
                                style={inputStyle("salesPrice")}
                                type="number"
                                name="salesPrice"
                                value={newSale.salesPrice}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "salesPrice", e.target.value)
                                }
                                {...focusHandlers}
                            />
                            {err("salesPrice")}
                        </div>

                        <div style={fieldWrap}>
                            <label style={labelStyle("paymentMethod")}>Payment Method</label>
                            <select
                                style={inputStyle("paymentMethod")}
                                name="paymentMethod"
                                value={newSale.paymentMethod}
                                onChange={(e) =>
                                    handleUpdate(setNewSale, "paymentMethod", e.target.value)
                                }
                                {...focusHandlers}
                            >
                                <option value="">Select</option>
                                <option value="Cash">Cash</option>
                                <option value="Cheque">Cheque</option>
                                <option value="Credit Card">Credit Card</option>
                                <option value="Debit">Debit</option>
                                <option value="E-Transfer">E-Transfer</option>
                            </select>
                            {err("paymentMethod")}
                        </div>
                    </div>

                    <div style={{ maxWidth: "14rem", ...fieldWrap }}>
                        <label style={lbl}>Amount Paid</label>
                        <input
                            style={inputBase}
                            type="number"
                            name="amount"
                            value={payment.amount}
                            onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                            {...focusHandlers}
                        />
                    </div>

                    <div style={fieldWrap}>
                        <label style={lbl}>Notes</label>
                        <textarea
                            style={{ ...inputBase, resize: "vertical" }}
                            name="notes"
                            value={note.description}
                            onChange={(e) => setNote({ ...note, description: e.target.value })}
                            rows={4}
                            placeholder="Any notes for this sale..."
                            {...focusHandlers}
                        />
                    </div>
                </>
            )}
        </form>
    );
};

export default FormSale;
